'use client'
import type { Data } from '@repo/strapi'
import { useTranslations } from 'next-intl'
import { useRef } from 'react'
import AppLink from '@/components/elementary/AppLink'
import { KnockAiLazy } from '@/components/elementary/footer/KnockAiLazy'
import { Tag } from '@/components/elementary/Tag'
import { Tooltip } from '@/components/elementary/Tooltip'
import { Info } from '@/components/icons/Info'
import PricingCompareCell from '@/components/page-builder/global/pricing/PricingCompareCell'
import Heading from '@/components/typography/Heading'
import { isRefSticky } from '@/hooks/useSticky'
import { type PricingPlanKey, pricingPlan } from '@/lib/pricing'
import { cn } from '@/lib/styles'
import { PRICING_TEST_IDS } from '@/lib/test-ids'

export const PricingCompareContent = ({
	cardsData,
	currentTab,
	className,
}: {
	cardsData?: Data.Component<'shared.pricing-item'>[] | null
	currentTab?: string | null
	className?: string
}) => {
	const t = useTranslations('pricing')
	const tPlan = useTranslations('pricingPlan')

	const colsStyleAll = { gridTemplateColumns: `264px repeat(${cardsData?.length}, minmax(0, 1fr))` }
	const colsStyleCurrent = { gridTemplateColumns: '1fr 1fr' }
	const colsStyle = currentTab ? colsStyleCurrent : colsStyleAll
	const commonGridPartsClass =
		'border-x-[0.5px] first:border-l-0 border-primary last:border-r-0 bg-peach-200 items-center'

	const cardsDataFiltered = currentTab
		? cardsData?.filter((item) => item.label === currentTab)
		: cardsData

	const stickyRef = useRef<HTMLDivElement>(null)
	const isStuck = isRefSticky(stickyRef, 100)
	return (
		<div className={cn(className)} data-test-id={PRICING_TEST_IDS.COMPARE_CONTENT}>
			<div className="grid" style={colsStyle}>
				<div className={cn('rounded-tl-xl', commonGridPartsClass)} />
				{cardsDataFiltered?.map((item) => (
					<div
						key={item?.id}
						className={cn(
							'pt-5 last:rounded-tr-xl ',
							commonGridPartsClass,
							item.isPopularTagVisible && 'lg:bg-peach-300',
						)}
					/>
				))}
			</div>
			<div
				ref={stickyRef}
				className={cn('grid lg:sticky top-25 z-2 ', isStuck && 'lg:border-b lg:border-brand-black')}
				style={colsStyle}
			>
				<div className={commonGridPartsClass} />
				{cardsDataFiltered?.map((item) => (
					<div
						key={item?.id}
						className={cn(
							'pb-2 px-2 pt-3 w-full text-center relative flex flex-wrap gap-x-2 gap-y-1 justify-center',
							commonGridPartsClass,
							'items-start',
							item.isPopularTagVisible && 'lg:bg-peach-300',
						)}
					>
						<Heading key={item?.id} tag="h2" fontSize="headingPricingTitle">
							{item?.label}
						</Heading>
						{item?.isPopularTagVisible && (
							<Tag
								name={t('mostPopular')}
								size="medium"
								className="w-fit"
								fontWeight="font-bold"
								active
								customColors={{ backgroundColor: 'neutral-light-100', borderColor: 'brand-black' }}
							/>
						)}
					</div>
				))}
			</div>
			<div className="grid" style={colsStyle}>
				<div className={commonGridPartsClass} />
				{cardsDataFiltered?.map((item) => {
					return (
						<div
							key={item?.id}
							className={cn(
								'px-4 w-full pt-1',
								commonGridPartsClass,
								item.isPopularTagVisible && 'lg:bg-peach-300',
							)}
						>
							{item?.link?.href && (
								<AppLink
									openExternalInNewTab={item.link?.newTab ?? false}
									href={item.link?.href}
									variant="primary"
									size="medium"
									className="w-full"
								>
									{item.link?.label}
								</AppLink>
							)}
							{item?.knockAiId && <KnockAiLazy dataId={item?.knockAiId} />}
						</div>
					)
				})}
			</div>
			<div className="rounded-b-xl overflow-hidden">
				{pricingPlan?.map((plan, index) => (
					<div key={`plan-${index}-${plan}`} className="grid" style={colsStyle}>
						<div
							className={cn(
								'p-4 flex justify-start text-neutral-500 break-word',
								commonGridPartsClass,
							)}
						>
							{tPlan(`${plan as PricingPlanKey}.label`)}
							{tPlan.has(`${plan as PricingPlanKey}.tooltip`) &&
							tPlan(`${plan as PricingPlanKey}.tooltip`)?.length > 0 ? (
								<Tooltip content={tPlan(`${plan as PricingPlanKey}.tooltip`)}>
									<span>
										<Info className="text-neutral-light-600 ml-1 size-5 shrink-0" />
									</span>
								</Tooltip>
							) : null}
						</div>
						{cardsDataFiltered?.map((item) => (
							<div
								key={item?.id}
								className={cn(
									'px-4 py-4 flex justify-center',
									commonGridPartsClass,
									item.isPopularTagVisible && 'lg:bg-peach-300',
								)}
							>
								<PricingCompareCell planItem={item?.plan?.find((p) => p.item?.item === plan)} />
							</div>
						))}
					</div>
				))}
			</div>
		</div>
	)
}

export default PricingCompareContent
